import { STATE_NAMES } from '../core/states';
import type { ValidationReason, Visibility } from '../core/types';
import type { OwnerCertification } from './api';

/** Words the page shows, kept apart from the components so each rule is one small function. */

/** Up to two letters from the first and last words of the name; a placeholder mark otherwise. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0]!.charAt(0);
  const last = words.length > 1 ? words[words.length - 1]!.charAt(0) : '';
  return (first + last).toUpperCase();
}

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS = MONTH_NAMES.map((name) => name.slice(0, 3));

/** `2021-08` → `Aug 2021`. Anything else is shown as it came. */
export function formatMonth(value: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  const month = match ? MONTHS[Number(match[2]) - 1] : undefined;
  return match && month ? `${month} ${match[1]}` : value;
}

export const jobDates = (startMonth: string, endMonth: string | null) => `${formatMonth(startMonth)} – ${endMonth ? formatMonth(endMonth) : 'Present'}`;

/** The day a badge was checked, as the visitor reads it: `Oct 22, 2026`. The date is UTC's. */
export function formatCheckedDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** "Verified by 240 as <checked name>, <date>" (KTD7, R19). */
export const badgeText = (checkedName: string, checkedAt: string) => `Verified by 240 as ${checkedName}, ${formatCheckedDate(checkedAt)}`;

/** Who a link reaches (R10). The visibility options and the share line say the same thing. */
export const AUDIENCE: Record<Visibility, { label: string; line: string }> = {
  hidden: { label: 'Hidden', line: 'Only you can open this link' },
  labs: { label: '240 Labs', line: 'Anyone signed in to 240 Labs' },
  public: { label: 'Public', line: 'Anyone with the link' },
};

/**
 * What the owner is told about one certificate's check, between pressing Verify and the
 * answer, and after it. A notice is this session's answer that the payload does not hold
 * (a refusal before any lookup, a cap, the switch); otherwise the saved result speaks.
 */
export type VerifyNotice =
  | { kind: 'checking' }
  | { kind: 'paused' }
  | { kind: 'rate_limited'; retryAfter: string | null }
  | { kind: 'refused'; error: string }
  | { kind: 'unreachable' };

const REFUSALS: Record<string, string> = {
  name_mismatch: 'The name on the certificate must match your profile name.',
  name_incomplete: 'Add your first and last name as the certificate shows them.',
  license_required: 'Add the Florida certificate number to verify.',
  not_verifiable: 'Only Texas and Florida certificates can be verified.',
};

const REASONS: Record<string, string> = {
  no_match: "We couldn't find this certificate. Check the details and try again.",
  lapsed: 'The state lists this certificate as not currently valid.',
  name_shared: 'More than one Texas educator has this name. Add your middle name as the state lists it.',
  name_mismatch: REFUSALS.name_mismatch!,
  state_unavailable: "The state site didn't answer. Try again later.",
};

/** A time of day the owner can act on: `3:45 PM`. */
function formatTime(iso: string | null): string {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return 'an hour';
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function verifyMessage(verification: OwnerCertification['verification'], notice: VerifyNotice | null): string | null {
  if (notice) {
    switch (notice.kind) {
      case 'checking': return 'Checking with the state…';
      case 'paused': return 'Verification is paused.';
      case 'rate_limited': return `Too many checks. Try again after ${formatTime(notice.retryAfter)}.`;
      case 'refused': return REFUSALS[notice.error] ?? REASONS.no_match!;
      case 'unreachable': return REASONS.state_unavailable!;
    }
  }
  if (!verification || verification.status === 'verified') return null;
  if (verification.reason && Object.hasOwn(REASONS, verification.reason)) return REASONS[verification.reason]!;
  if (verification.status === 'ambiguous') return REASONS.name_shared!;
  if (verification.status === 'error') return REASONS.state_unavailable!;
  return REASONS.no_match!;
}

/** Inline messages for a refused field (`ValidationReason` in `core/types.ts`). */
export function fieldMessage(label: string, reason: ValidationReason, max?: number): string {
  switch (reason) {
    case 'required': return `Enter the ${label.toLowerCase()}.`;
    case 'before_start': return 'The end date can’t be before the start date.';
    case 'out_of_range': return `Enter a ${label.toLowerCase()} between 1950 and this year.`;
    case 'too_long': return max ? `Keep this under ${max} characters.` : 'This is too long.';
    default: return `Enter a valid ${label.toLowerCase()}.`;
  }
}

/** The state a certificate is from, by name (`core/states.ts`). */
export const stateName = (code: string) => STATE_NAMES[code] ?? code;
